import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { ReactNode, useEffect, useState } from 'react';
import { Button, Container, InputGroup } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { LocationButton } from '../../components/buttons/LocationButton';
import { useMyLocationContext } from '../../context';
import { LocationSelection } from '../../services/helper';

export type LocationSetterVariant = 'default' | 'desktop' | 'mobile';

interface TypeaheadPlacementOptions {
  dropup: boolean;
  flip: boolean;
  positionFixed: boolean;
  align: 'justify' | 'left' | 'right';
  inputId: string;
}

const LOCATION_SETTER_VARIANTS: Record<
  LocationSetterVariant,
  { className?: string; placement: TypeaheadPlacementOptions }
> = {
  default: {
    placement: {
      dropup: false,
      flip: true,
      positionFixed: false,
      align: 'justify',
      inputId: 'location'
    }
  },
  desktop: {
    className: 'sfc-location-typeahead',
    placement: {
      align: 'right',
      positionFixed: true,
      dropup: false,
      flip: true,
      inputId: 'location'
    }
  },
  mobile: {
    className: 'sfc-location-typeahead sfc-navbar-menu-footer__location-input',
    placement: {
      align: 'left',
      positionFixed: true,
      dropup: true,
      flip: true,
      inputId: 'location-mobile'
    }
  }
};

export interface LocationSearchProps {
  setLocation: (search: LocationSelection, valid: boolean) => void;
  button: ReactNode;
  current?: boolean;
  dropup?: boolean;
  flip?: boolean;
  positionFixed?: boolean;
  align?: 'justify' | 'left' | 'right';
  inputId?: string;
}

export const LocationSearch: React.FC<LocationSearchProps> = ({
                                                                setLocation,
                                                                button,
                                                                current = false,
                                                                dropup = false,
                                                                flip = true,
                                                                positionFixed = false,
                                                                align = 'justify',
                                                                inputId = 'location'
                                                              }) => {
  const { myLocation } = useMyLocationContext();
  const GeocodingLibrary = useMapsLibrary('geocoding');
  const [geocodingService, setGeocodingService] = useState<google.maps.Geocoder>();
  const [locationResults, setLocationResults] = useState<Array<LocationSelection>>();

  useEffect(() => {
    if (!GeocodingLibrary) return;
    setGeocodingService(new GeocodingLibrary.Geocoder());
  }, [GeocodingLibrary]);

  const preset = current
      ? [
        {
          label: myLocation?.label ?? '',
          value: JSON.stringify({ label: myLocation?.label, location: myLocation?.location })
        }
      ]
      : [];

  const getPlaceOptions = (search: string) => {
    if (!geocodingService) return;
    if (geocodingService) {
      geocodingService.geocode(
          { address: search, componentRestrictions: { country: 'US' }, region: 'US' },
          (results) => {
        let data =
            results?.map((r) => {
              let zipIndex = r.address_components.findIndex((ac) => ac.types.includes('postal_code'));
              let [zip] = zipIndex >= 0 ? r.address_components.splice(zipIndex, 1) : [undefined];
              let shortLabel = r.formatted_address;
              let label = zip ? String(zip.short_name) : shortLabel;
              return {
                label: `${zip ? shortLabel : label}`,
                value: JSON.stringify({ location: r.geometry.location.toJSON(), label })
              };
            }) ?? [];

        setLocationResults([...preset, ...data]);
      });
    }
  };

  return (
      <>
        <Typeahead
            id={inputId}
            placeholder="Set by zip code or city"
            options={locationResults ?? []}
            defaultSelected={current ? preset : undefined}
            dropup={dropup}
            flip={flip}
            positionFixed={positionFixed}
            align={align}
            onChange={(option) => {
              const selected = option?.[0] as LocationSelection;
              setLocation(selected, !!selected);
            }}
            onInputChange={getPlaceOptions}
        />
        {button}
      </>
  );
};

interface LocationButtonFormProps {
  dropup?: boolean;
  flip?: boolean;
  positionFixed?: boolean;
  align?: 'justify' | 'left' | 'right';
  inputId?: string;
  variant?: LocationSetterVariant;
}

export const LocationButtonForm: React.FC<LocationButtonFormProps> = ({
  dropup,
  flip,
  positionFixed,
  align,
  inputId,
  variant = 'default'
}) => {
  const isMobileFooter = variant === 'mobile';
  const { myLocation, saveMyLocation } = useMyLocationContext();
  const [showForm, setShowForm] = useState<boolean>(false);

  const savePreferredLocation = (search: LocationSelection, valid: boolean) => {
    const setLoc = saveMyLocation(search.value, true);
    if (valid && setLoc) {
      setShowForm(false);
    }
  };

  const button = (
      <Button
          variant="sfc-blueberry"
          onClick={() => setShowForm(false)}
      >
        X
      </Button>
  );

  const form = (
      <LocationSearch
          setLocation={savePreferredLocation}
          button={button}
          dropup={dropup}
          flip={flip}
          positionFixed={positionFixed}
          align={align}
          inputId={inputId}
      />
  );

  const myLocationButton = (
      <LocationButton
          size="sm"
          width={isMobileFooter ? undefined : 120}
          className={isMobileFooter ? 'sfc-mobile-footer-location-btn' : undefined}
          onClick={() => setShowForm(true)}
      >
      <span
          className={isMobileFooter ? 'sfc-mobile-footer-location-label' : undefined}
          style={
            isMobileFooter
                ? undefined
                : {
                  maxWidth: 80,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '12px'
                }
          }
      >
        {myLocation?.label}
      </span>
      </LocationButton>
  );

  const setLocationButton = (
      <LocationButton
          size="sm"
          onClick={() => setShowForm(true)}
      >
        Set Location
      </LocationButton>
  );

  return showForm ? form : myLocation ? myLocationButton : setLocationButton;
};

interface LocationSetterProps {
  className?: string;
  variant?: LocationSetterVariant;
}

export const LocationSetter: React.FC<LocationSetterProps> = ({
  className,
  variant = 'default'
}) => {
  const { className: variantClassName, placement } = LOCATION_SETTER_VARIANTS[variant];
  const containerClassName = [variantClassName, className].filter(Boolean).join(' ') || undefined;

  return (
      <Container
          className={containerClassName}
          style={variant === 'mobile' ? { maxWidth: '100%', padding: 0 } : { maxWidth: '160px' }}
      >
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_API_KEY!}>
          <InputGroup
              size="sm"
              className={variant === 'mobile' ? 'sfc-mobile-footer-location-input-group' : undefined}
          >
            <LocationButtonForm
                dropup={placement.dropup}
                flip={placement.flip}
                positionFixed={placement.positionFixed}
                align={placement.align}
                inputId={placement.inputId}
                variant={variant}
            />
          </InputGroup>
        </APIProvider>
      </Container>
  );
};

export const LocationSetterNoForm: React.FC<LocationSearchProps> = ({ setLocation, button }) => {
  return (
      <>
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_API_KEY!}>
          <InputGroup size="sm">
            <LocationSearch
                setLocation={setLocation}
                button={button}
                current={true}
            />
          </InputGroup>
        </APIProvider>
      </>
  );
};